import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, Filter, Plus, ChevronDown, ArrowDownToLine, ArrowUpFromLine,
  XCircle, MoreHorizontal, AlertTriangle, UserX, ChevronLeft, ChevronRight, Trash2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { VESSEL_FLAG_OPTIONS, ADMIN_REFUND_MIN_HOURS_BEFORE_ARRIVAL } from '@harbormaster/shared';
import { api, ApiError } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { useParkingModule } from '../hooks/useParkingModule';
import { Modal } from '../components/Modal';
import { getDateLocale, getLanguage } from '../i18n';

interface BookingRow {
  id: string;
  booking_code: string;
  berth_id: number | null;
  guest_name: string;
  guest_email: string;
  guest_phone: string | null;
  vessel_name: string;
  vessel_length_m: number | string;
  vessel_beam_m: number | string | null;
  vessel_draft_m: number | string | null;
  electricity: boolean;
  people_on_board: number | null;
  vessel_registration: string | null;
  vessel_flag: string | null;
  start_datetime: string;
  end_datetime: string;
  status: string;
  total_price: number | string;
  currency: string;
  booking_source: string;
  language: string | null;
  access_codes_sent: boolean | null;
  notes: string | null;
  payment_status: string;
  payment_id: number | null;
  created_at: string;
  updated_at?: string | null;
  berths?: { label: string } | null;
}

interface BerthOption {
  id: number;
  label: string;
  is_active: boolean;
}

interface ParkingSpotOption {
  id: number;
  label: string;
  spot_type: string;
}

interface ParkingBookingRow {
  id: number;
  booking_code: string;
  spot_id: number | null;
  guest_name: string;
  guest_email: string;
  guest_phone: string | null;
  vehicle_reg: string | null;
  spot_type: string;
  start_datetime: string;
  end_datetime: string;
  status: string;
  total_price: number | string;
  currency: string;
  booking_source: string;
  language: string | null;
  notes: string | null;
  payment_status?: string | null;
  payment_id?: number | null;
  created_at: string;
  updated_at?: string | null;
  parking_spots?: { label: string; spot_type: string } | null;
}

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  unpaid: 'bg-gray-100 text-gray-600',
  pending_payment: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
  refunded: 'bg-purple-100 text-purple-800',
  partially_refunded: 'bg-indigo-100 text-indigo-800',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  pending_payment: 'bg-orange-100 text-orange-800',
  confirmed: 'bg-blue-100 text-blue-800',
  checked_in: 'bg-green-100 text-green-800',
  checked_out: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
  no_show: 'bg-orange-100 text-orange-800',
};

const ALL_STATUSES = ['pending', 'confirmed', 'checked_in', 'checked_out', 'cancelled', 'no_show'];

/** Parking list filter (aligned with admin API). */
const ALL_PARKING_STATUSES = ['pending', 'pending_payment', 'confirmed', 'checked_in', 'checked_out', 'cancelled', 'no_show'];

/** Quick date window: overlaps stay (start/end) — computed on server via marina timezone. */
const BOOKING_PERIOD_KEYS = ['all', 'today', 'week', 'month'] as const;
type BookingPeriodKey = (typeof BOOKING_PERIOD_KEYS)[number];

const MS_PER_HOUR = 60 * 60 * 1000;

/** Paid bookings only; blocks checked-in/out, ended stays, and arrivals within ADMIN_REFUND_MIN_HOURS_BEFORE_ARRIVAL. */
function canAdminRefundBooking(b: BookingRow): boolean {
  if (b.payment_status !== 'paid') return false;
  if (b.status === 'checked_out' || b.status === 'checked_in') return false;

  const now = Date.now();
  const end = new Date(b.end_datetime).getTime();
  if (Number.isNaN(end) || end < now) return false;

  const start = new Date(b.start_datetime).getTime();
  if (Number.isNaN(start)) return false;

  if (start > now) {
    const hoursUntilStart = (start - now) / MS_PER_HOUR;
    if (hoursUntilStart < ADMIN_REFUND_MIN_HOURS_BEFORE_ARRIVAL) return false;
  }

  return true;
}

/** Matches action menu item visibility so we do not show an empty "⋯" menu. */
function bookingRowHasActions(b: BookingRow, canWrite: boolean): boolean {
  const hasCheckIn = b.status === 'confirmed' || b.status === 'pending';
  const hasCheckOut = b.status === 'checked_in';
  const hasRefund = canAdminRefundBooking(b);
  const hasCancel = b.status !== 'cancelled' && b.status !== 'checked_out';
  const hasNoShow =
    b.status !== 'no_show' && b.status !== 'checked_in' && b.status !== 'checked_out' && b.status !== 'cancelled';
  const hasDelete = b.status === 'cancelled' && canWrite;
  return hasCheckIn || hasCheckOut || hasRefund || hasCancel || hasNoShow || hasDelete;
}

function parkingRowHasActions(b: ParkingBookingRow): boolean {
  const hasCheckIn = b.status === 'confirmed' || b.status === 'pending' || b.status === 'pending_payment';
  const hasCheckOut = b.status === 'checked_in';
  const hasCancel = b.status !== 'cancelled' && b.status !== 'checked_out';
  const hasNoShow =
    b.status !== 'no_show' &&
    b.status !== 'checked_in' &&
    b.status !== 'checked_out' &&
    b.status !== 'cancelled';
  return hasCheckIn || hasCheckOut || hasCancel || hasNoShow;
}

function formatMetric(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

function vesselFlagLabel(code: string | null | undefined): string {
  if (!code) return '—';
  const lang = getLanguage();
  const opt = VESSEL_FLAG_OPTIONS.find((o) => o.code === code);
  if (!opt) return code;
  return lang === 'et' ? opt.labelEt : opt.labelEn;
}

function formatBookingInstant(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(getDateLocale(), { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function localDatetimeInputToIso(v: string): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

export function BookingsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { activeMarinaId, canWrite } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { parkingEnabled, loading: parkingModLoading } = useParkingModule();

  const bookingTab = searchParams.get('tab') === 'parking' ? 'parking' : 'marina';
  const periodParam = searchParams.get('period');
  const periodFilter: BookingPeriodKey =
    periodParam === 'today' || periodParam === 'week' || periodParam === 'month' ? periodParam : 'all';

  const setBookingTab = (tab: 'marina' | 'parking') => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'parking') next.set('tab', 'parking');
    else next.delete('tab');
    setSearchParams(next);
  };

  const setPeriodFilter = (p: BookingPeriodKey) => {
    const next = new URLSearchParams(searchParams);
    if (p === 'all') next.delete('period');
    else next.set('period', p);
    setSearchParams(next);
  };

  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [berths, setBerths] = useState<BerthOption[]>([]);
  const [parkingBookings, setParkingBookings] = useState<ParkingBookingRow[]>([]);
  const [parkingSpots, setParkingSpots] = useState<ParkingSpotOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [parkingTotal, setParkingTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [berthFilter, setBerthFilter] = useState('');
  const [spotFilter, setSpotFilter] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const PAGE_SIZE = 50;
  const [page, setPage] = useState(0);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddParkingModal, setShowAddParkingModal] = useState(false);
  /** Open row action menu key: marina booking UUID or `p-{id}` for parking. */
  const [actionMenu, setActionMenu] = useState<string | null>(null);
  /** Viewport anchor for fixed-position menu (escapes table overflow clipping). */
  const [actionMenuRect, setActionMenuRect] = useState<DOMRect | null>(null);
  const [cancelTarget, setCancelTarget] = useState<BookingRow | null>(null);
  const [cancelParkingTarget, setCancelParkingTarget] = useState<ParkingBookingRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BookingRow | null>(null);
  const [detailMarinaBooking, setDetailMarinaBooking] = useState<BookingRow | null>(null);
  const [detailParkingBooking, setDetailParkingBooking] = useState<ParkingBookingRow | null>(null);
  const initialLoadRef = useRef(true);
  const parkingInitialLoadRef = useRef(true);
  const prevFiltersRef = useRef({ search, statusFilter, berthFilter, periodFilter });
  const prevParkingFiltersRef = useRef({ search, statusFilter, spotFilter, periodFilter });

  const toastRef = useRef(toast);
  toastRef.current = toast;

  const closeActionMenu = useCallback(() => {
    setActionMenu(null);
    setActionMenuRect(null);
  }, []);

  const fetchBookings = useCallback(async (s = search, st = statusFilter, bf = berthFilter, p = page) => {
    try {
      const params: Record<string, string> = {
        limit: String(PAGE_SIZE),
        offset: String(p * PAGE_SIZE),
      };
      if (s.trim()) params.search = s.trim();
      if (st) params.status = st;
      if (bf) params.berth_id = bf;
      if (periodFilter !== 'all') params.period = periodFilter;
      const data = await api.getBookings(params);
      setBookings(data.bookings as unknown as BookingRow[]);
      setTotal(data.total);
    } catch (err) {
      if (err instanceof ApiError) toastRef.current.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, berthFilter, periodFilter, page, activeMarinaId]);

  const fetchParkingBookings = useCallback(async (s = search, st = statusFilter, sf = spotFilter, p = page) => {
    try {
      const params: Record<string, string> = {
        limit: String(PAGE_SIZE),
        offset: String(p * PAGE_SIZE),
      };
      if (s.trim()) params.search = s.trim();
      if (st) params.status = st;
      if (sf) params.spot_id = sf;
      if (periodFilter !== 'all') params.period = periodFilter;
      const data = await api.getParkingBookings(params);
      setParkingBookings(data.bookings as unknown as ParkingBookingRow[]);
      setParkingTotal(data.total);
    } catch (err) {
      if (err instanceof ApiError) toastRef.current.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, spotFilter, periodFilter, page, activeMarinaId]);

  useEffect(() => {
    if (!parkingEnabled && bookingTab === 'parking') {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('tab');
          return next;
        },
        { replace: true },
      );
    }
  }, [parkingEnabled, bookingTab, setSearchParams]);

  useEffect(() => {
    setLoading(true);
  }, [bookingTab]);

  useEffect(() => {
    if (bookingTab === 'marina') {
      initialLoadRef.current = true;
      prevFiltersRef.current = { search, statusFilter, berthFilter, periodFilter };
    } else {
      parkingInitialLoadRef.current = true;
      prevParkingFiltersRef.current = { search, statusFilter, spotFilter, periodFilter };
    }
  }, [bookingTab]);

  useEffect(() => {
    api.getBerths()
      .then((d) => setBerths((d.berths as unknown as BerthOption[]).filter((b) => b.is_active)))
      .catch(() => { /* berth filter unavailable — non-critical */ });
  }, [activeMarinaId]);

  useEffect(() => {
    if (!parkingEnabled || bookingTab !== 'parking') return;
    api.getParkingSpots()
      .then((d) => {
        const list = (d.spots as { id: number; label: string; is_active: boolean; spot_type: string }[])
          .filter((s) => s.is_active)
          .map((s) => ({ id: s.id, label: s.label, spot_type: String(s.spot_type ?? '') }));
        setParkingSpots(list);
      })
      .catch(() => { setParkingSpots([]); });
  }, [parkingEnabled, bookingTab, activeMarinaId]);

  useEffect(() => {
    if (bookingTab !== 'marina') return;
    const prev = prevFiltersRef.current;
    const filtersChanged =
      prev.search !== search ||
      prev.statusFilter !== statusFilter ||
      prev.berthFilter !== berthFilter ||
      prev.periodFilter !== periodFilter;
    prevFiltersRef.current = { search, statusFilter, berthFilter, periodFilter };

    const scheduleFetch = () => {
      setLoading(true);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchBookings(), 300);
    };

    if (filtersChanged) {
      // setPage(0) is a no-op when already on page 0 — still refetch with new filters.
      if (page !== 0) {
        setPage(0);
        return;
      }
      scheduleFetch();
      return () => clearTimeout(debounceRef.current);
    }
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      fetchBookings();
      return;
    }
    scheduleFetch();
    return () => clearTimeout(debounceRef.current);
  }, [bookingTab, search, statusFilter, berthFilter, periodFilter, page, fetchBookings]);

  useEffect(() => {
    if (bookingTab !== 'parking' || !parkingEnabled) return;
    const prev = prevParkingFiltersRef.current;
    const filtersChanged =
      prev.search !== search ||
      prev.statusFilter !== statusFilter ||
      prev.spotFilter !== spotFilter ||
      prev.periodFilter !== periodFilter;
    prevParkingFiltersRef.current = { search, statusFilter, spotFilter, periodFilter };

    const scheduleFetch = () => {
      setLoading(true);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchParkingBookings(), 300);
    };

    if (filtersChanged) {
      if (page !== 0) {
        setPage(0);
        return;
      }
      scheduleFetch();
      return () => clearTimeout(debounceRef.current);
    }
    if (parkingInitialLoadRef.current) {
      parkingInitialLoadRef.current = false;
      fetchParkingBookings();
      return;
    }
    scheduleFetch();
    return () => clearTimeout(debounceRef.current);
  }, [bookingTab, parkingEnabled, search, statusFilter, spotFilter, periodFilter, page, fetchParkingBookings]);

  const handleStatusChange = async (booking: BookingRow, newStatus: string) => {
    closeActionMenu();
    try {
      await api.updateBookingStatus(booking.id, newStatus);
      toast.success(`${booking.booking_code} → ${newStatus.replace('_', ' ')}`);
      fetchBookings();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    }
  };

  const handleParkingStatusChange = async (booking: ParkingBookingRow, newStatus: string) => {
    closeActionMenu();
    try {
      await api.updateParkingBookingStatus(booking.id, newStatus);
      toast.success(`${booking.booking_code} → ${newStatus.replace('_', ' ')}`);
      fetchParkingBookings();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    }
  };

  const handleDeletePermanent = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteBooking(deleteTarget.id);
      toast.success(t('bookings.bookingDeleted', { code: deleteTarget.booking_code }));
      setDeleteTarget(null);
      fetchBookings();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    }
  };

  if (parkingModLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  const listTotal = bookingTab === 'parking' ? parkingTotal : total;
  const hasFilters =
    bookingTab === 'parking'
      ? !!(search || statusFilter || spotFilter || periodFilter !== 'all')
      : !!(search || statusFilter || berthFilter || periodFilter !== 'all');

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl font-bold text-gray-900">{t('bookings.title')}</h1>
        <div className="flex flex-wrap gap-2 self-start">
          {bookingTab === 'marina' && (
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
            >
              <Plus className="w-4 h-4" /> {t('bookings.addBooking')}
            </button>
          )}
          {parkingEnabled && bookingTab === 'parking' && (
            <button
              type="button"
              onClick={() => setShowAddParkingModal(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
            >
              <Plus className="w-4 h-4" /> {t('bookings.addParkingBooking')}
            </button>
          )}
        </div>
      </div>

      {parkingEnabled && (
        <div className="flex rounded-lg border border-gray-200 p-0.5 bg-gray-50/80 w-fit">
          <button
            type="button"
            onClick={() => setBookingTab('marina')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              bookingTab === 'marina' ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('bookings.tabMarina')}
          </button>
          <button
            type="button"
            onClick={() => setBookingTab('parking')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              bookingTab === 'parking' ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('bookings.tabParking')}
          </button>
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <p className="text-xs text-gray-500 pt-2 max-w-xl">{t('bookings.periodHint')}</p>
        <div
          role="tablist"
          aria-label={t('bookings.periodAriaLabel')}
          className="flex shrink-0 rounded-lg border border-gray-200 p-0.5 bg-gray-50/80 flex-wrap gap-0.5 w-full sm:w-auto"
        >
          {BOOKING_PERIOD_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={periodFilter === key}
              onClick={() => {
                if (key === periodFilter) return;
                setPeriodFilter(key);
                setPage(0);
              }}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                periodFilter === key ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t(`bookings.period.${key}` as Parameters<typeof t>[0])}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={bookingTab === 'parking' ? t('bookings.searchPlaceholderParking') : t('bookings.searchPlaceholder')}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="pl-8 pr-8 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/40 appearance-none"
          >
            <option value="">{t('bookings.allStatuses')}</option>
            {(bookingTab === 'parking' ? ALL_PARKING_STATUSES : ALL_STATUSES).map((s) => (
              <option key={s} value={s}>{t(`status.${s}` as Parameters<typeof t>[0], { defaultValue: s.replace(/_/g, ' ') })}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        </div>
        {bookingTab === 'marina' ? (
          <div className="relative">
            <select
              value={berthFilter}
              onChange={(e) => setBerthFilter(e.target.value)}
              className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/40 appearance-none"
            >
              <option value="">{t('bookings.allBerths')}</option>
              {berths.map((b) => (
                <option key={b.id} value={String(b.id)}>{b.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          </div>
        ) : (
          <div className="relative">
            <select
              value={spotFilter}
              onChange={(e) => setSpotFilter(e.target.value)}
              className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/40 appearance-none"
            >
              <option value="">{t('bookings.allParkingSpots')}</option>
              {parkingSpots.map((s) => (
                <option key={s.id} value={String(s.id)}>{s.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200">
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 min-h-[280px]" role="status" aria-live="polite">
            <div className="w-9 h-9 border-4 border-brand-100 border-t-brand-600 rounded-full animate-spin" />
            <p className="text-sm text-gray-500">{t('common.loading')}</p>
          </div>
        ) : bookingTab === 'marina' && bookings.length === 0 ? (
          <div className="px-5 py-16 text-center text-sm text-gray-400">
            {hasFilters ? t('bookings.noBookingsFiltered') : t('bookings.noBookings')}
          </div>
        ) : bookingTab === 'parking' && parkingBookings.length === 0 ? (
          <div className="px-5 py-16 text-center text-sm text-gray-400">
            {hasFilters ? t('bookings.noParkingFiltered') : t('bookings.noParkingBookings')}
          </div>
        ) : bookingTab === 'marina' ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('bookings.code')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('bookings.guest')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide hidden sm:table-cell">{t('bookings.vessel')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('bookings.berth')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide hidden md:table-cell">{t('bookings.dates')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('bookings.status')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide hidden lg:table-cell">{t('bookings.price')}</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bookings.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setDetailMarinaBooking(b)}
                        className="font-mono text-xs font-semibold text-brand-600 hover:text-brand-800 hover:underline text-left focus:outline-none focus:ring-2 focus:ring-brand-400/60 rounded px-0.5 -mx-0.5"
                        aria-label={t('bookings.viewDetailsAria', { code: b.booking_code })}
                      >
                        {b.booking_code}
                      </button>
                    </td>
                    <td className="px-4 py-3 min-w-[10rem] max-w-[220px]">
                      <div className="text-gray-900 truncate" title={b.guest_name}>{b.guest_name}</div>
                      <div className="text-xs text-gray-400 truncate" title={b.guest_email}>{b.guest_email}</div>
                      <div className="text-xs text-gray-700 truncate tabular-nums" title={b.guest_phone ?? undefined}>
                        {b.guest_phone?.trim() ? b.guest_phone : <span className="text-gray-400">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 hidden sm:table-cell">
                      <div className="truncate max-w-[100px]">{b.vessel_name}</div>
                      <div className="text-xs text-gray-400">{b.vessel_length_m}m{b.electricity ? ' ⚡' : ''}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{b.berths?.label ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap hidden md:table-cell">
                      {formatBookingInstant(b.start_datetime)} → {formatBookingInstant(b.end_datetime)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${STATUS_COLORS[b.status] ?? 'bg-gray-100'}`}>
                        {t(`status.${b.status}` as Parameters<typeof t>[0], { defaultValue: b.status.replace('_', ' ') })}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap hidden lg:table-cell">
                      <div>{b.total_price} {b.currency}</div>
                      {b.payment_status && b.payment_status !== 'unpaid' && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PAYMENT_STATUS_COLORS[b.payment_status] ?? 'bg-gray-100 text-gray-600'}`}>
                          {t(`paymentStatus.${b.payment_status}` as Parameters<typeof t>[0], { defaultValue: b.payment_status.replace('_', ' ') })}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="relative inline-block">
                        {bookingRowHasActions(b, canWrite) ? (
                          <>
                        <button
                          type="button"
                          onClick={(e) => {
                            if (actionMenu === b.id) closeActionMenu();
                            else {
                              setActionMenu(b.id);
                              setActionMenuRect(e.currentTarget.getBoundingClientRect());
                            }
                          }}
                          className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                        {actionMenu === b.id && actionMenuRect ? (
                          <>
                            <div className="fixed inset-0 z-40" aria-hidden onClick={closeActionMenu} />
                            <div
                              role="menu"
                              className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[176px]"
                              style={{
                                top: actionMenuRect.bottom + 6,
                                left: Math.max(8, actionMenuRect.right - 176),
                              }}
                            >
                              {(b.status === 'confirmed' || b.status === 'pending') && (
                                <button
                                  onClick={() => handleStatusChange(b, 'checked_in')}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <ArrowDownToLine className="w-3.5 h-3.5 text-green-600" /> {t('dashboard.checkIn')}
                                </button>
                              )}
                              {b.status === 'checked_in' && (
                                <button
                                  onClick={() => handleStatusChange(b, 'checked_out')}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <ArrowUpFromLine className="w-3.5 h-3.5 text-gray-600" /> {t('dashboard.checkOut')}
                                </button>
                              )}
                              {canAdminRefundBooking(b) && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    closeActionMenu();
                                    if (!window.confirm(t('bookings.refundConfirm', { code: b.booking_code }))) return;
                                    try {
                                      const result = await api.refundBooking(b.id);
                                      toast.success(t('bookings.refunded', { amount: result.refunded_amount, currency: result.currency, code: b.booking_code }));
                                      void fetchBookings();
                                    } catch (err) {
                                      if (err instanceof ApiError) {
                                        if (err.code === 'REFUND_TOO_CLOSE_TO_ARRIVAL') {
                                          toast.error(t('bookings.refundErrorTooClose', { hours: ADMIN_REFUND_MIN_HOURS_BEFORE_ARRIVAL }));
                                        } else if (err.code === 'REFUND_NOT_ALLOWED_CHECKED_IN') {
                                          toast.error(t('bookings.refundErrorCheckedIn'));
                                        } else if (err.code === 'REFUND_PAST_OR_COMPLETED') {
                                          toast.error(t('bookings.refundErrorPastOrCompleted'));
                                        } else {
                                          toast.error(err.message);
                                        }
                                      } else {
                                        toast.error(t('errors.somethingWentWrong'));
                                      }
                                    }
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm text-purple-600 hover:bg-purple-50 flex items-center gap-2"
                                >
                                  💸 {t('bookings.refundPayment')}
                                </button>
                              )}
                              {b.status !== 'cancelled' && b.status !== 'checked_out' && (
                                <button
                                  onClick={() => { closeActionMenu(); setCancelTarget(b); }}
                                  className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                >
                                  <XCircle className="w-3.5 h-3.5" /> {t('common.cancel')}
                                </button>
                              )}
                              {b.status !== 'no_show' && b.status !== 'checked_in' && b.status !== 'checked_out' && b.status !== 'cancelled' && (
                                <button
                                  type="button"
                                  onClick={() => handleStatusChange(b, 'no_show')}
                                  className="w-full text-left px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2"
                                >
                                  <UserX className="w-3.5 h-3.5" /> {t('bookings.noShow')}
                                </button>
                              )}
                              {b.status === 'cancelled' && canWrite && (
                                <button
                                  type="button"
                                  onClick={() => { closeActionMenu(); setDeleteTarget(b); }}
                                  className="w-full text-left px-3 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center gap-2"
                                >
                                  <Trash2 className="w-3.5 h-3.5" /> {t('bookings.deletePermanently')}
                                </button>
                              )}
                            </div>
                          </>
                        ) : null}
                          </>
                        ) : (
                          <span className="text-gray-300 select-none" aria-hidden>—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('bookings.code')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('bookings.guest')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide hidden sm:table-cell">{t('bookings.vehicleReg')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('bookings.parkingSpot')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide hidden md:table-cell">{t('bookings.dates')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('bookings.status')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide hidden lg:table-cell">{t('bookings.price')}</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {parkingBookings.map((b) => {
                  const pk = `p-${b.id}`;
                  return (
                    <tr key={b.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setDetailParkingBooking(b)}
                          className="font-mono text-xs font-semibold text-brand-600 hover:text-brand-800 hover:underline text-left focus:outline-none focus:ring-2 focus:ring-brand-400/60 rounded px-0.5 -mx-0.5"
                          aria-label={t('bookings.viewDetailsAria', { code: b.booking_code })}
                        >
                          {b.booking_code}
                        </button>
                      </td>
                      <td className="px-4 py-3 min-w-[10rem] max-w-[220px]">
                        <div className="text-gray-900 truncate" title={b.guest_name}>{b.guest_name}</div>
                        <div className="text-xs text-gray-400 truncate" title={b.guest_email}>{b.guest_email}</div>
                        <div className="text-xs text-gray-700 truncate tabular-nums" title={b.guest_phone ?? undefined}>
                          {b.guest_phone?.trim() ? b.guest_phone : <span className="text-gray-400">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700 hidden sm:table-cell font-mono text-xs">{b.vehicle_reg ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                        <div>{b.parking_spots?.label ?? '—'}</div>
                        <div className="text-xs text-gray-400">{t(`parkingSpots.types.${b.spot_type}` as Parameters<typeof t>[0])}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap hidden md:table-cell">
                        {formatBookingInstant(b.start_datetime)} → {formatBookingInstant(b.end_datetime)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${STATUS_COLORS[b.status] ?? 'bg-gray-100'}`}>
                          {t(`status.${b.status}` as Parameters<typeof t>[0], { defaultValue: b.status.replace(/_/g, ' ') })}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap hidden lg:table-cell">
                        <div>{b.total_price} {b.currency}</div>
                        {b.payment_status && b.payment_status !== 'unpaid' && (
                          <span className={`inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PAYMENT_STATUS_COLORS[b.payment_status] ?? 'bg-gray-100 text-gray-600'}`}>
                            {t(`paymentStatus.${b.payment_status}` as Parameters<typeof t>[0], { defaultValue: b.payment_status.replace('_', ' ') })}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="relative inline-block">
                          {parkingRowHasActions(b) ? (
                            <>
                              <button
                                type="button"
                                onClick={(e) => {
                                  if (actionMenu === pk) closeActionMenu();
                                  else {
                                    setActionMenu(pk);
                                    setActionMenuRect(e.currentTarget.getBoundingClientRect());
                                  }
                                }}
                                className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </button>
                              {actionMenu === pk && actionMenuRect ? (
                                <>
                                  <div className="fixed inset-0 z-40" aria-hidden onClick={closeActionMenu} />
                                  <div
                                    role="menu"
                                    className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[176px]"
                                    style={{
                                      top: actionMenuRect.bottom + 6,
                                      left: Math.max(8, actionMenuRect.right - 176),
                                    }}
                                  >
                                    {(b.status === 'confirmed' || b.status === 'pending' || b.status === 'pending_payment') && (
                                      <button
                                        type="button"
                                        onClick={() => handleParkingStatusChange(b, 'checked_in')}
                                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                      >
                                        <ArrowDownToLine className="w-3.5 h-3.5 text-green-600" /> {t('dashboard.checkIn')}
                                      </button>
                                    )}
                                    {b.status === 'checked_in' && (
                                      <button
                                        type="button"
                                        onClick={() => handleParkingStatusChange(b, 'checked_out')}
                                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                      >
                                        <ArrowUpFromLine className="w-3.5 h-3.5 text-gray-600" /> {t('dashboard.checkOut')}
                                      </button>
                                    )}
                                    {b.status !== 'cancelled' && b.status !== 'checked_out' && (
                                      <button
                                        type="button"
                                        onClick={() => { closeActionMenu(); setCancelParkingTarget(b); }}
                                        className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                      >
                                        <XCircle className="w-3.5 h-3.5" /> {t('common.cancel')}
                                      </button>
                                    )}
                                    {b.status !== 'no_show' && b.status !== 'checked_in' && b.status !== 'checked_out' && b.status !== 'cancelled' && (
                                      <button
                                        type="button"
                                        onClick={() => handleParkingStatusChange(b, 'no_show')}
                                        className="w-full text-left px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2"
                                      >
                                        <UserX className="w-3.5 h-3.5" /> {t('bookings.noShow')}
                                      </button>
                                    )}
                                  </div>
                                </>
                              ) : null}
                            </>
                          ) : (
                            <span className="text-gray-300 select-none" aria-hidden>—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {listTotal > 0 && !loading && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
            <span>
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, listTotal)} {t('common.of')} {listTotal}{' '}
              {bookingTab === 'parking' ? t('bookings.parkingBookingsLower') : t('nav.bookings').toLowerCase()}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => p - 1)}
                disabled={page === 0}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-2">{t('common.page')} {page + 1} {t('common.of')} {Math.ceil(listTotal / PAGE_SIZE) || 1}</span>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * PAGE_SIZE >= listTotal}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add booking modal */}
      <AddBookingModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        berths={berths}
        onCreated={() => { setShowAddModal(false); fetchBookings(); }}
      />

      <AddParkingBookingModal
        open={showAddParkingModal}
        onClose={() => setShowAddParkingModal(false)}
        spots={parkingSpots}
        onCreated={() => { setShowAddParkingModal(false); fetchParkingBookings(); }}
      />

      {/* Cancel confirmation modal */}
      <Modal
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        title={t('bookings.cancelBooking')}
        maxWidth="max-w-sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-700">
                {t('bookings.cancelBooking')} <span className="font-mono font-semibold">{cancelTarget?.booking_code}</span>?
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {cancelTarget?.guest_name} — {cancelTarget?.vessel_name}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{t('common.thisActionCannotBeUndone')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setCancelTarget(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              {t('bookings.keepBooking')}
            </button>
            <button
              onClick={() => {
                if (cancelTarget) {
                  handleStatusChange(cancelTarget, 'cancelled');
                  setCancelTarget(null);
                }
              }}
              className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
            >
              {t('bookings.cancelBooking')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!cancelParkingTarget}
        onClose={() => setCancelParkingTarget(null)}
        title={t('bookings.cancelBooking')}
        maxWidth="max-w-sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-700">
                {t('bookings.cancelBooking')} <span className="font-mono font-semibold">{cancelParkingTarget?.booking_code}</span>?
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {cancelParkingTarget?.guest_name} — {cancelParkingTarget?.vehicle_reg ?? '—'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{t('common.thisActionCannotBeUndone')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setCancelParkingTarget(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              {t('bookings.keepBooking')}
            </button>
            <button
              type="button"
              onClick={() => {
                if (cancelParkingTarget) {
                  handleParkingStatusChange(cancelParkingTarget, 'cancelled');
                  setCancelParkingTarget(null);
                }
              }}
              className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
            >
              {t('bookings.cancelBooking')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Permanent delete (cancelled only) */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('bookings.deleteBookingTitle')}
        maxWidth="max-w-sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-700">
                {t('bookings.deleteBookingBody', { code: deleteTarget?.booking_code ?? '' })}
              </p>
              {deleteTarget?.payment_status === 'paid' && (
                <p className="text-xs text-amber-700 mt-2">{t('bookings.deleteBookingPaidWarning')}</p>
              )}
              <p className="text-xs text-gray-500 mt-2">{t('bookings.deleteBookingIrreversible')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setDeleteTarget(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => { void handleDeletePermanent(); }}
              className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
            >
              {t('bookings.deletePermanently')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!detailMarinaBooking}
        onClose={() => setDetailMarinaBooking(null)}
        title={t('bookings.bookingDetails')}
        maxWidth="max-w-xl"
      >
        {detailMarinaBooking ? <MarinaBookingDetailBody booking={detailMarinaBooking} /> : null}
      </Modal>

      <Modal
        open={!!detailParkingBooking}
        onClose={() => setDetailParkingBooking(null)}
        title={t('bookings.parkingBookingDetails')}
        maxWidth="max-w-xl"
      >
        {detailParkingBooking ? <ParkingBookingDetailBody booking={detailParkingBooking} /> : null}
      </Modal>
    </div>
  );
}

function stripRequiredMarks(label: string): string {
  return label.replace(/\s*\*+\s*$/u, '').trim();
}

function DetailSectionTitle({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <h3 className={`text-xs font-semibold text-gray-400 uppercase tracking-wide mt-5 mb-2 border-b border-gray-100 pb-1 ${className}`.trim()}>
      {children}
    </h3>
  );
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,9.5rem)_1fr] gap-x-3 gap-y-0.5 text-sm py-2 border-b border-gray-50">
      <div className="text-gray-500 shrink-0">{label}</div>
      <div className="text-gray-900 break-words min-w-0">{value}</div>
    </div>
  );
}

function MarinaBookingDetailBody({ booking: b }: { booking: BookingRow }) {
  const { t } = useTranslation();

  const yn = (v: boolean | null | undefined): string =>
    v ? t('common.yes') : v === false ? t('common.no') : '—';

  return (
    <div>
      <p className="text-sm font-mono font-semibold text-brand-700 mb-3">{b.booking_code}</p>
      <div>
        <DetailSectionTitle className="mt-0">{t('bookings.guest')}</DetailSectionTitle>
        <DetailField label={stripRequiredMarks(t('bookings.guestName'))} value={b.guest_name} />
        <DetailField label={stripRequiredMarks(t('bookings.email'))} value={b.guest_email} />
        <DetailField label={stripRequiredMarks(t('bookings.phone'))} value={b.guest_phone?.trim() ? b.guest_phone : '—'} />

        <DetailSectionTitle>{t('bookings.vessel')}</DetailSectionTitle>
        <DetailField label={stripRequiredMarks(t('bookings.vesselName'))} value={b.vessel_name} />
        <DetailField label={stripRequiredMarks(t('bookings.lengthM'))} value={`${formatMetric(b.vessel_length_m)} m`} />
        <DetailField label={stripRequiredMarks(t('bookings.beamM'))} value={`${formatMetric(b.vessel_beam_m)} m`} />
        <DetailField label={stripRequiredMarks(t('bookings.draftM'))} value={`${formatMetric(b.vessel_draft_m)} m`} />
        <DetailField label={stripRequiredMarks(t('bookings.vesselRegistration'))} value={b.vessel_registration ?? '—'} />
        <DetailField label={stripRequiredMarks(t('bookings.vesselFlag'))} value={vesselFlagLabel(b.vessel_flag)} />
        <DetailField label={stripRequiredMarks(t('bookings.peopleOnBoard'))} value={b.people_on_board != null ? String(b.people_on_board) : '—'} />
        <DetailField label={t('bookings.electricity')} value={yn(b.electricity)} />

        <DetailSectionTitle>{t('bookings.berth')}</DetailSectionTitle>
        <DetailField label={t('bookings.berth')} value={b.berths?.label ?? '—'} />

        <DetailSectionTitle>{t('bookings.dates')}</DetailSectionTitle>
        <DetailField label={stripRequiredMarks(t('bookings.arrivalDateTime'))} value={formatBookingInstant(b.start_datetime)} />
        <DetailField label={stripRequiredMarks(t('bookings.departureDateTime'))} value={formatBookingInstant(b.end_datetime)} />

        <DetailSectionTitle>{t('bookings.statusPriceSection')}</DetailSectionTitle>
        <DetailField
          label={t('bookings.status')}
          value={(
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[b.status] ?? 'bg-gray-100'}`}>
              {t(`status.${b.status}` as Parameters<typeof t>[0], { defaultValue: b.status.replace(/_/g, ' ') })}
            </span>
          )}
        />
        <DetailField
          label={t('bookings.price')}
          value={(
            <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>{b.total_price} {b.currency}</span>
              {b.payment_status && b.payment_status !== 'unpaid' && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PAYMENT_STATUS_COLORS[b.payment_status] ?? 'bg-gray-100'}`}>
                  {t(`paymentStatus.${b.payment_status}` as Parameters<typeof t>[0], { defaultValue: b.payment_status })}
                </span>
              )}
            </span>
          )}
        />
        <DetailField
          label={t('bookings.bookingSource')}
          value={t(`bookings.source.${b.booking_source}` as Parameters<typeof t>[0], { defaultValue: b.booking_source.replace(/_/g, ' ') })}
        />
        <DetailField label={t('bookings.languageLabel')} value={b.language ?? '—'} />
        <DetailField label={t('bookings.accessCodesSent')} value={yn(b.access_codes_sent)} />

        <DetailSectionTitle>{t('bookings.notesSection')}</DetailSectionTitle>
        <div className="text-sm text-gray-900 whitespace-pre-wrap py-2">{b.notes?.trim() ? b.notes : '—'}</div>

        <DetailSectionTitle>{t('bookings.metaSection')}</DetailSectionTitle>
        <DetailField label={t('bookings.internalId')} value={<span className="font-mono text-xs break-all">{b.id}</span>} />
        <DetailField label={t('bookings.createdAt')} value={formatBookingInstant(b.created_at)} />
        <DetailField label={t('bookings.updatedAt')} value={b.updated_at ? formatBookingInstant(b.updated_at) : '—'} />
      </div>
    </div>
  );
}

function ParkingBookingDetailBody({ booking: b }: { booking: ParkingBookingRow }) {
  const { t } = useTranslation();
  const pay = b.payment_status ?? 'unpaid';

  const spotLabel = b.parking_spots?.label ?? '—';

  return (
    <div>
      <p className="text-sm font-mono font-semibold text-brand-700 mb-3">{b.booking_code}</p>
      <div>
        <DetailSectionTitle className="mt-0">{t('bookings.guest')}</DetailSectionTitle>
        <DetailField label={stripRequiredMarks(t('bookings.guestName'))} value={b.guest_name} />
        <DetailField label={stripRequiredMarks(t('bookings.email'))} value={b.guest_email} />
        <DetailField label={stripRequiredMarks(t('bookings.phone'))} value={b.guest_phone?.trim() ? b.guest_phone : '—'} />

        <DetailSectionTitle>{t('bookings.parkingInfoSection')}</DetailSectionTitle>
        <DetailField label={t('bookings.vehicleReg')} value={b.vehicle_reg ?? '—'} />
        <DetailField
          label={t('bookings.parkingSpot')}
          value={(
            <div>
              <div>{spotLabel}</div>
              <div className="text-xs text-gray-500">{t(`parkingSpots.types.${b.spot_type}` as Parameters<typeof t>[0])}</div>
            </div>
          )}
        />

        <DetailSectionTitle>{t('bookings.dates')}</DetailSectionTitle>
        <DetailField label={stripRequiredMarks(t('bookings.arrivalDateTime'))} value={formatBookingInstant(b.start_datetime)} />
        <DetailField label={stripRequiredMarks(t('bookings.departureDateTime'))} value={formatBookingInstant(b.end_datetime)} />

        <DetailSectionTitle>{t('bookings.statusPriceSection')}</DetailSectionTitle>
        <DetailField
          label={t('bookings.status')}
          value={(
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[b.status] ?? 'bg-gray-100'}`}>
              {t(`status.${b.status}` as Parameters<typeof t>[0], { defaultValue: b.status.replace(/_/g, ' ') })}
            </span>
          )}
        />
        <DetailField
          label={t('bookings.price')}
          value={(
            <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>{b.total_price} {b.currency}</span>
              {pay !== 'unpaid' && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PAYMENT_STATUS_COLORS[pay] ?? 'bg-gray-100'}`}>
                  {t(`paymentStatus.${pay}` as Parameters<typeof t>[0], { defaultValue: pay })}
                </span>
              )}
            </span>
          )}
        />
        <DetailField
          label={t('bookings.bookingSource')}
          value={t(`bookings.source.${b.booking_source}` as Parameters<typeof t>[0], { defaultValue: b.booking_source.replace(/_/g, ' ') })}
        />
        <DetailField label={t('bookings.languageLabel')} value={b.language ?? '—'} />

        <DetailSectionTitle>{t('bookings.notesSection')}</DetailSectionTitle>
        <div className="text-sm text-gray-900 whitespace-pre-wrap py-2">{b.notes?.trim() ? b.notes : '—'}</div>

        <DetailSectionTitle>{t('bookings.metaSection')}</DetailSectionTitle>
        <DetailField label={t('bookings.internalId')} value={<span className="font-mono text-xs">{b.id}</span>} />
        <DetailField label={t('bookings.createdAt')} value={formatBookingInstant(b.created_at)} />
        <DetailField label={t('bookings.updatedAt')} value={b.updated_at ? formatBookingInstant(b.updated_at) : '—'} />
      </div>
    </div>
  );
}

// ── Add Booking Modal ─────────────────────────────────────────────────

interface AddBookingModalProps {
  open: boolean;
  onClose: () => void;
  berths: BerthOption[];
  onCreated: () => void;
}

const EMPTY_BOOKING_FORM = {
  berth_id: '',
  guest_name: '',
  guest_email: '',
  guest_phone: '',
  people_on_board: '2',
  vessel_registration: '',
  vessel_flag: 'EE',
  vessel_name: '',
  vessel_length_m: '',
  vessel_beam_m: '',
  electricity: false,
  start_datetime: '',
  end_datetime: '',
  notes: '',
};

function AddBookingModal({ open, onClose, berths, onCreated }: AddBookingModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_BOOKING_FORM);

  useEffect(() => {
    if (open) setForm(EMPTY_BOOKING_FORM);
  }, [open]);

  const set = (key: string, value: string | boolean) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const startIso = localDatetimeInputToIso(form.start_datetime);
    const endIso = localDatetimeInputToIso(form.end_datetime);
    if (!startIso || !endIso || new Date(endIso) <= new Date(startIso)) {
      toast.error(t('bookings.invalidDateRange'));
      return;
    }
    const pob = parseInt(form.people_on_board, 10);
    if (!Number.isFinite(pob) || pob < 1 || pob > 500) {
      toast.error(t('bookings.invalidPeopleOnBoard'));
      return;
    }
    setSubmitting(true);
    try {
      await api.createBooking({
        berth_id: parseInt(form.berth_id, 10),
        guest_name: form.guest_name,
        guest_email: form.guest_email,
        guest_phone: form.guest_phone.trim(),
        people_on_board: pob,
        vessel_registration: form.vessel_registration.trim(),
        vessel_flag: form.vessel_flag.trim().toUpperCase(),
        vessel_name: form.vessel_name,
        vessel_length_m: parseFloat(form.vessel_length_m),
        vessel_beam_m: form.vessel_beam_m ? parseFloat(form.vessel_beam_m) : undefined,
        electricity: form.electricity,
        start_datetime: startIso,
        end_datetime: endIso,
        notes: form.notes || undefined,
        language: 'en',
      });
      toast.success(t('bookings.bookingCreated'));
      onCreated();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('bookings.failedToCreate'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40';
  const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <Modal open={open} onClose={onClose} title={t('bookings.addBookingTitle')} maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>{t('bookings.arrivalDateTime')}</label>
            <input
              type="datetime-local"
              value={form.start_datetime}
              onChange={(e) => {
                set('start_datetime', e.target.value);
                if (form.end_datetime && e.target.value && form.end_datetime <= e.target.value) {
                  set('end_datetime', '');
                }
              }}
              required
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>{t('bookings.departureDateTime')}</label>
            <input
              type="datetime-local"
              value={form.end_datetime}
              onChange={(e) => set('end_datetime', e.target.value)}
              required
              min={form.start_datetime || undefined}
              className={inputClass}
            />
          </div>
        </div>
        <div>
          <label className={labelClass}>{t('bookings.berth')} *</label>
          <select value={form.berth_id} onChange={(e) => set('berth_id', e.target.value)} required className={inputClass}>
            <option value="">{t('bookings.selectBerth')}</option>
            {berths.map((b) => (
              <option key={b.id} value={b.id}>{b.label}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>{t('bookings.guestName')}</label>
            <input type="text" value={form.guest_name} onChange={(e) => set('guest_name', e.target.value)} required maxLength={100} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t('bookings.email')}</label>
            <input type="email" value={form.guest_email} onChange={(e) => set('guest_email', e.target.value)} required maxLength={254} className={inputClass} />
          </div>
        </div>
        <div>
          <label className={labelClass}>{t('bookings.phone')}</label>
          <input type="tel" value={form.guest_phone} onChange={(e) => set('guest_phone', e.target.value)} required maxLength={50} className={inputClass} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>{t('bookings.peopleOnBoard')}</label>
            <input
              type="number"
              min={1}
              max={500}
              value={form.people_on_board}
              onChange={(e) => set('people_on_board', e.target.value)}
              required
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>{t('bookings.vesselFlag')}</label>
            <select value={form.vessel_flag} onChange={(e) => set('vessel_flag', e.target.value)} required className={inputClass}>
              {VESSEL_FLAG_OPTIONS.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.code} — {o.labelEn}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className={labelClass}>{t('bookings.vesselRegistration')}</label>
          <input
            type="text"
            value={form.vessel_registration}
            onChange={(e) => set('vessel_registration', e.target.value)}
            required
            maxLength={64}
            className={inputClass}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>{t('bookings.vesselName')}</label>
            <input type="text" value={form.vessel_name} onChange={(e) => set('vessel_name', e.target.value)} required maxLength={100} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t('bookings.lengthM')}</label>
            <input type="number" step="0.1" min="1" max="50" value={form.vessel_length_m} onChange={(e) => set('vessel_length_m', e.target.value)} required className={inputClass} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>{t('bookings.beamM')}</label>
            <input type="number" step="0.1" min="0.5" max="15" value={form.vessel_beam_m} onChange={(e) => set('vessel_beam_m', e.target.value)} className={inputClass} />
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.electricity} onChange={(e) => set('electricity', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-brand-600" />
              <span className="text-sm text-gray-700">{t('bookings.electricity')}</span>
            </label>
          </div>
        </div>
        <div>
          <label className={labelClass}>{t('common.notes')}</label>
          <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} maxLength={500} className={inputClass} />
        </div>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={submitting} className="flex-1 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
            {submitting ? t('bookings.creating') : t('bookings.createBooking')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Add parking booking (admin) ───────────────────────────────────────

interface AddParkingBookingModalProps {
  open: boolean;
  onClose: () => void;
  spots: ParkingSpotOption[];
  onCreated: () => void;
}

const EMPTY_PARKING_BOOKING_FORM = {
  spot_id: '',
  guest_name: '',
  guest_email: '',
  guest_phone: '',
  vehicle_reg: '',
  start_date: '',
  end_date: '',
};

function AddParkingBookingModal({ open, onClose, spots, onCreated }: AddParkingBookingModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_PARKING_BOOKING_FORM);

  useEffect(() => {
    if (open) setForm(EMPTY_PARKING_BOOKING_FORM);
  }, [open]);

  const set = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || spots.length === 0) return;
    const spot = spots.find((s) => String(s.id) === form.spot_id);
    if (!spot?.spot_type) {
      toast.error(t('bookings.selectParkingSpot'));
      return;
    }
    if (!form.start_date || !form.end_date || form.end_date <= form.start_date) {
      toast.error(t('bookings.parkingInvalidDates'));
      return;
    }
    setSubmitting(true);
    try {
      await api.createParkingBooking({
        spot_id: spot.id,
        spot_type: spot.spot_type,
        guest_name: form.guest_name.trim(),
        guest_email: form.guest_email.trim(),
        guest_phone: form.guest_phone.trim(),
        vehicle_reg: form.vehicle_reg.trim(),
        start_date: form.start_date,
        end_date: form.end_date,
        language: getLanguage(),
      });
      toast.success(t('bookings.parkingBookingCreated'));
      onCreated();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('bookings.failedToCreate'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40';
  const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <Modal open={open} onClose={onClose} title={t('bookings.addParkingBookingTitle')} maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-3">
        {spots.length === 0 ? (
          <p className="text-sm text-amber-700">{t('bookings.noParkingSpotsForBooking')}</p>
        ) : null}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>{t('bookings.parkingStartDate')}</label>
            <input
              type="date"
              value={form.start_date}
              onChange={(e) => {
                set('start_date', e.target.value);
                if (form.end_date && e.target.value && form.end_date <= e.target.value) {
                  set('end_date', '');
                }
              }}
              required
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>{t('bookings.parkingEndDate')}</label>
            <input
              type="date"
              value={form.end_date}
              min={form.start_date || undefined}
              onChange={(e) => set('end_date', e.target.value)}
              required
              className={inputClass}
            />
          </div>
        </div>
        <div>
          <label className={labelClass}>{t('bookings.parkingSpot')} *</label>
          <select
            value={form.spot_id}
            onChange={(e) => set('spot_id', e.target.value)}
            required
            disabled={spots.length === 0}
            className={inputClass}
          >
            <option value="">{t('bookings.selectParkingSpot')}</option>
            {spots.map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.label} ({t(`parkingSpots.types.${s.spot_type}` as Parameters<typeof t>[0], { defaultValue: s.spot_type })})
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>{t('bookings.guestName')}</label>
            <input type="text" value={form.guest_name} onChange={(e) => set('guest_name', e.target.value)} required maxLength={255} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t('bookings.email')}</label>
            <input type="email" value={form.guest_email} onChange={(e) => set('guest_email', e.target.value)} required maxLength={254} className={inputClass} />
          </div>
        </div>
        <div>
          <label className={labelClass}>{t('bookings.phone')}</label>
          <input type="tel" value={form.guest_phone} onChange={(e) => set('guest_phone', e.target.value)} required maxLength={50} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>{t('bookings.vehicleReg')}</label>
          <input type="text" value={form.vehicle_reg} onChange={(e) => set('vehicle_reg', e.target.value)} required maxLength={50} className={inputClass} />
        </div>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || spots.length === 0}
            className="flex-1 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
          >
            {submitting ? t('bookings.creating') : t('bookings.createParkingBooking')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
